import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { quotesAPI } from '../../services/api';
import SalespersonLayout from '../../layouts/SalespersonLayout';

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const response = await quotesAPI.getById(id);
      setQuote(response.data.quote);
    } catch (err) {
      setError('Error al cargar la cotización');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await quotesAPI.getPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${quote.quote_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al descargar PDF. Intente de nuevo.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDuplicate = async () => {
    if (!window.confirm('¿Duplicar esta cotización?')) return;
    try {
      const response = await quotesAPI.duplicate(id);
      navigate(`/quotes/${response.data.quote.id}`);
    } catch (err) {
      alert('Error al duplicar cotización');
    }
  };

  if (loading) {
    return (
      <SalespersonLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </SalespersonLayout>
    );
  }

  if (error || !quote) {
    return (
      <SalespersonLayout>
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error || 'Cotizacion no encontrada'}</p>
          <button onClick={() => navigate('/quotes')} className="btn btn-primary">
            Volver a Cotizaciones
          </button>
        </div>
      </SalespersonLayout>
    );
  }

  return (
    <SalespersonLayout>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{quote.quote_number}</h2>
          <p className="text-sm text-gray-500">{quote.web_name}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(`/quotes/${id}/edit`)}
            className="btn btn-secondary"
          >
            Editar
          </button>
          <button
            onClick={handleDuplicate}
            className="btn btn-secondary"
          >
            Duplicar
          </button>
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="btn btn-primary"
          >
            {downloading ? 'Descargando...' : 'Descargar PDF'}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto space-y-6">
        {/* Quote Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Cliente</h3>
              <p className="font-semibold text-gray-900">{quote.client_name}</p>
              {quote.client_email && <p className="text-sm text-gray-600">{quote.client_email}</p>}
              {quote.client_phone && <p className="text-sm text-gray-600">{quote.client_phone}</p>}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Fecha</h3>
              <p className="font-semibold text-gray-900">
                {new Date(quote.quote_date).toLocaleDateString('es-MX')}
              </p>
              <p className="text-sm text-gray-600">
                Válido hasta: {new Date(quote.valid_until).toLocaleDateString('es-MX')}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Cotizador</h3>
              <p className="font-semibold text-gray-900">{quote.user_name}</p>
              <p className="text-sm text-gray-600">{quote.user_email}</p>
            </div>
          </div>
        </div>

        {/* Bank & RFC Info */}
        {(quote.bank_name || quote.rfc_code) && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Datos Fiscales y Bancarios</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {quote.bank_name && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Banco</h3>
                  <p className="font-semibold text-gray-900">{quote.bank_name}</p>
                  <p className="text-sm text-gray-600">Cuenta: {quote.account_number}</p>
                  <p className="text-sm text-gray-600">CLABE: {quote.clabe}</p>
                  <p className="text-sm text-gray-600">Titular: {quote.holder_name}</p>
                </div>
              )}
              {quote.rfc_code && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">RFC</h3>
                  <p className="font-semibold text-gray-900 font-mono">{quote.rfc_code}</p>
                  <p className="text-sm text-gray-600">{quote.business_name}</p>
                  {quote.fiscal_regime && (
                    <p className="text-sm text-gray-600">{quote.fiscal_regime}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Productos</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Precio c/IVA</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {quote.products?.map((product, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                    {product.product_code || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {product.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                    ${parseFloat(product.price_with_iva).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                    {product.quantity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                    ${parseFloat(product.total).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="max-w-xs ml-auto space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-medium">${parseFloat(quote.subtotal).toFixed(2)}</span>
            </div>
            {parseFloat(quote.discount_percent) > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Descuento ({quote.discount_percent}%):</span>
                <span>-${parseFloat(quote.discount_amount).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">IVA (16%):</span>
              <span className="font-medium">${parseFloat(quote.iva_amount).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xl pt-3 border-t">
              <span className="font-bold">Total:</span>
              <span className="font-bold text-primary-600">${parseFloat(quote.total).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </SalespersonLayout>
  );
};

export default QuoteDetail;
